import { IMaskInput } from 'react-imask'
import { cn } from "@/lib/class-control"
import { ControllerRenderProps, FieldValues } from "react-hook-form"

interface InputCpfCnpjProps<T extends FieldValues> {
  field: ControllerRenderProps<T>
  placeholder?: string
  className?: string
  classContainer?: string
  disabled?: boolean
}

const cpfMask = '000.000.000-00'
const cnpjMask = '00.000.000/0000-00'

export default function InputCpfCnpj<T extends FieldValues>({
  field,
  placeholder = "Digite o CPF ou CNPJ",
  className,
  ...props
}: InputCpfCnpjProps<T>) {
  return (
    <div className={cn("flex items-center w-full px-3 py-2 border border-gray-600 rounded",
      "bg-transparent text-white focus-within:ring-1 focus-within:ring-green-400",
      props?.disabled && "opacity-50 bg-[#0c1427] cursor-not-allowed",
      props.classContainer)
    }>
      <IMaskInput
        mask={[
          { mask: cpfMask },
          { mask: cnpjMask }
        ]}
        dispatch={(appended, dynamicMasked) => {
          const value = (dynamicMasked.value + appended).replace(/\D/g, '')
          return dynamicMasked.compiledMasks.find((m) => {
            const maskDigits = (m as { mask: string }).mask.replace(/\D/g, '').length
            if (value.length <= 11) {
              return m.mask === cpfMask
            }
            return m.mask === cnpjMask
          }) || dynamicMasked.compiledMasks[0]
        }}
        name={field.name}
        inputRef={field.ref}
        value={field.value == null ? "" : String(field.value)}
        onAccept={(value) => field.onChange(value)}
        placeholder={placeholder}
        className={cn(
          "flex-1 bg-transparent outline-none text-sm text-white placeholder-gray-400",
          "autofill:bg-transparent autofill:shadow-[0_0_0_1000px_#15203c_inset] autofill:[-webkit-text-fill-color:#fff]",
          "[&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:shadow-[0_0_0_1000px_#15203c_inset] [&:-webkit-autofill]:[-webkit-text-fill-color:#fff]",
          "[&:-webkit-autofill:hover]:shadow-[0_0_0_1000px_#15203c_inset] [&:-webkit-autofill:focus]:shadow-[0_0_0_1000px_#15203c_inset]",
          props?.disabled && "cursor-not-allowed",
          className
        )}
        {...props}
      />
    </div>
  )
}

InputCpfCnpj.displayName = "InputCpfCnpj"

// Funções utilitárias para validação
export function isCpf(value: string): boolean {
  const digits = value.replace(/\D/g, '')
  return digits.length === 11
}

export function isCnpj(value: string): boolean {
  const digits = value.replace(/\D/g, '')
  return digits.length === 14
}

export function validateCpf(cpf: string): boolean {
  const digits = cpf.replace(/\D/g, '')

  if (digits.length !== 11) return false

  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(digits)) return false

  // Validação do primeiro dígito verificador
  let sum = 0
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits[i]) * (10 - i)
  }
  let remainder = (sum * 10) % 11
  if (remainder === 10 || remainder === 11) remainder = 0
  if (remainder !== parseInt(digits[9])) return false

  // Validação do segundo dígito verificador
  sum = 0
  for (let i = 0; i < 10; i++) {
    sum += parseInt(digits[i]) * (11 - i)
  }
  remainder = (sum * 10) % 11
  if (remainder === 10 || remainder === 11) remainder = 0
  if (remainder !== parseInt(digits[10])) return false

  return true
}

export function validateCnpj(cnpj: string): boolean {
  const digits = cnpj.replace(/\D/g, '')

  if (digits.length !== 14) return false

  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(digits)) return false

  // Validação do primeiro dígito verificador
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
  let sum = 0
  for (let i = 0; i < 12; i++) {
    sum += parseInt(digits[i]) * weights1[i]
  }
  let remainder = sum % 11
  const digit1 = remainder < 2 ? 0 : 11 - remainder
  if (digit1 !== parseInt(digits[12])) return false

  // Validação do segundo dígito verificador
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
  sum = 0
  for (let i = 0; i < 13; i++) {
    sum += parseInt(digits[i]) * weights2[i]
  }
  remainder = sum % 11
  const digit2 = remainder < 2 ? 0 : 11 - remainder
  if (digit2 !== parseInt(digits[13])) return false

  return true
}

export function validateCpfCnpj(value: string): boolean {
  const digits = value.replace(/\D/g, '')

  if (digits.length === 11) {
    return validateCpf(value)
  }

  if (digits.length === 14) {
    return validateCnpj(value)
  }

  return false
}

export function getDocumentType(value: string): 'cpf' | 'cnpj' | null {
  const digits = value.replace(/\D/g, '')

  if (digits.length === 11) return 'cpf'
  if (digits.length === 14) return 'cnpj'

  return null
}
